import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import * as EmailValidator from 'email-validator';
import {
  Radio,
  RadioGroup,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  Chip,
  Typography,
} from '@material-ui/core';
import { Cancel } from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import DateSelect from '../../../components/DateSelect';
import Button from '../../../components/Button';
import PatientSearchBar from '../../../components/PatientSearchBar';
import PhoneInput from '../../../components/PhoneInput';
import { isEmailAvailable } from '../../../services/api';
import { ADULTHOOD_AGE } from '../../../constants';
import { fetchCurrentUser } from '../../../services/api/auth';

function generateString(length) {
  // program to generate random strings
  let characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = ' ';
  let charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}
function PatientProfile(props) {
  const {
    patient,
    onSubmit,
    onClose,
    backButtonText,
    source='',
    classes: { input, button, warning },
  } = props;
  const initGuardians = (patient.guardians || []).reduce((acc, guardian) => {
    acc[guardian.id] = guardian.name;
    return acc;
  }, {});

  const { enqueueSnackbar } = useSnackbar();
  var date = new Date();
  let minYears = date.setFullYear(date.getFullYear() - ADULTHOOD_AGE);

  const [firstName, setFirstName] = useState(patient.firstName || '');
  const [lastName, setLastName] = useState(patient.lastName || '');
  // const [email, setEmail] = useState(patient.email && !patient.email.includes('@example.com') ? patient.email :'');
  const [email, setEmail] = useState(patient.email ? patient.email : '');
  const [phone, setPhone] = useState(patient.phone || '');
  const [gender, setGender] = useState(patient.gender || '');
  const [birthdate, setBirthdate] = useState(patient.birthdate);
  const [zipcode, setZipcode] = useState(patient.zipcode || '');
  const [tebraId, setTebraId] = useState(patient.tebraPatientId || '');
  const [validationErrors, setValidationErrors] = useState({});
  const [guardians, setGuardians] = useState(initGuardians);
  const [showGuardianSelection, setShowGuardianSelection] = useState(false);
  const [currentUser, setCurrentUser] = useState(null); // To get the current user's info.

    useEffect(() => {
    async function loadUser() {
      const user = await fetchCurrentUser();
      setCurrentUser(user);
    }
    loadUser();
  }, []);

  useEffect(() => {
    setShowGuardianSelection(
      isMinor(birthdate) || Object.keys(guardians).length > 0
    );
  }, [guardians]);

  const isMinor = birthTime => {
    const now = new Date().getTime();
    const then = new Date(birthTime).getTime();
    return (now - then) / 1000 / 60 / 60 / 24 / 365 < ADULTHOOD_AGE;
  };

  const onChange = setter => event => {
    setter(event.target.value);
    setValidationErrors({ ...validationErrors, [event.target.name]: false });
  };

  const validate = () => {
    const guardianIds = Object.keys(guardians);
    let valid = true;
    const nextState = {};
    if (!firstName.replace(/^\s+|\s+$/gm, '')) {
      valid = false;
      nextState.firstName = 'Required';
    }
    if (!lastName.replace(/^\s+|\s+$/gm, '')) {
      valid = false;
      nextState.lastName = 'Required';
    }
    if (guardianIds.length === 0) {
      if (!email) {
        valid = false;
        nextState.email = 'Required';
      } else if (!EmailValidator.validate(email)) {
        valid = false;
        nextState.email = 'Must be a valid email';
      }
      if (!phone) {
        valid = false;
        nextState.phone = 'Required';
      }
      if (!zipcode) {
        valid = false;
        nextState.zipcode = 'Required';
      } else if (!zipcode.match(/^\d{5}$|^\d{5}-\d{4}$/)) {
        valid = false;
        nextState.zipcode = 'Must be a valid zipcode';
      }
    }
    if (!gender) {
      valid = false;
      nextState.gender = 'Required';
    }
    if (!birthdate) {
      valid = false;
      nextState.birthdate = 'Required';
    }
    setValidationErrors(nextState);
    return valid;
  };

  return (
    <div>
      <TextField
        fullWidth
        label="First Name"
        name="firstName"
        value={firstName}
        variant="outlined"
        onChange={onChange(setFirstName)}
        error={validationErrors.firstName}
        helperText={validationErrors.firstName}
        className={input}
      />

      <TextField
        fullWidth
        label="Last Name"
        value={lastName}
        variant="outlined"
        name="lastName"
        onChange={onChange(setLastName)}
        error={validationErrors.lastName}
        helperText={validationErrors.lastName}
        className={input}
      />

      <TextField
        fullWidth
        label="Email"
        value={email}
        variant="outlined"
        name="email"
        onChange={onChange(setEmail)}
        error={validationErrors.email}
        helperText={validationErrors.email}
        inputProps={{
          onBlur: async () => {
            const available = await isEmailAvailable(email);
            if (!available) {
              setValidationErrors({
                ...validationErrors,
                email: 'this email is already in use',
              });
            }
          },
        }}
        className={input}
      />

      <TextField
        fullWidth
        label="Phone Number"
        value={phone}
        variant="outlined"
        name="phone"
        onChange={onChange(setPhone)}
        error={validationErrors.phone}
        helperText={validationErrors.phone}
        className={input}
        InputProps={{
          inputComponent: PhoneInput,
        }}
      />

      <TextField
        fullWidth
        label="Zip Code"
        value={zipcode}
        name="zipecode"
        variant="outlined"
        onChange={onChange(setZipcode)}
        error={validationErrors.zipcode}
        helperText={validationErrors.zipcode}
        className={input}
      />

      {(currentUser?.admin && (source === 'patientProfilePage' || source === 'miniPatientProfile') ) && <TextField
        fullWidth
        label="Patient's Tebra ID"
        value={tebraId}
        name="tebraId"
        variant="outlined"
        onChange={onChange(setTebraId)}
        className={input}
        disabled={
          !(
            currentUser?.admin &&
            (source === 'patientProfilePage' || source === 'miniPatientProfile')
          )
        }
      />}

      <FormControl component="fieldset" variant="filled" className={input}>
        <FormLabel component="legend">
          Gender (as listed on health insurance card)
        </FormLabel>
        <RadioGroup
          row
          aria-label="Gender"
          name="gender"
          value={gender}
          onChange={onChange(setGender)}
        >
          <FormControlLabel value="female" control={<Radio />} label="Female" />
          <FormControlLabel value="male" control={<Radio />} label="Male" />
        </RadioGroup>
        {validationErrors.gender && (
          <FormLabel component="legend" className={warning}>
            {validationErrors.gender}
          </FormLabel>
        )}
      </FormControl>

      <DateSelect
        disableFuture
        fullWidth
        label="Birthdate"
        format="MM/DD/YYYY"
        maxDate={new Date(minYears)}
        placeholder="MM/DD/YYYY ex.10/02/1981"
        value={new Date(birthdate)}
        error={validationErrors.birthdate}
        helperText={validationErrors.birthdate}
        onChange={(date) => {
          setBirthdate(moment.utc(date).format('MM/DD/YYYY'));
          setShowGuardianSelection(isMinor(date));
          setValidationErrors({ ...validationErrors, birthdate: false });
        }}
      />

      {showGuardianSelection && (
        <>
          <PatientSearchBar
            label="Guardian"
            onSelect={(guardian) => {
              if (guardian) {
                setGuardians({
                  ...guardians,
                  [guardian.id]: `${guardian.firstName} ${guardian.lastName}`,
                });
              }
            }}
          />
          <div>
            {Object.keys(guardians).map((guardianId) => (
              <Chip
                key={guardianId}
                onDelete={() => {
                  delete guardians[guardianId];
                  setGuardians({ ...guardians });
                }}
                clickable
                deleteIcon={<Cancel />}
                label={guardians[guardianId]}
              />
            ))}
            {Object.keys(guardians).length === 0 && showGuardianSelection ? (
              <Typography className={warning}>Required</Typography>
            ) : (
              ''
            )}
          </div>
        </>
      )}

      <div>
        <Button
          onClick={onClose}
          variant="outlined"
          color="primary"
          className={button}
        >
          {backButtonText || 'Back'}
        </Button>

        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            if (showGuardianSelection && Object.keys(guardians).length === 0) {
              enqueueSnackbar('Please select Guardian', {
                variant: 'warning',
                preventDuplicate: true,
                autoHideDuration: 3000,
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'center',
                },
              });
            } else
              onSubmit({
                ...patient,
                firstName: firstName.replace(/^\s+|\s+$/gm, ''),
                lastName: lastName.replace(/^\s+|\s+$/gm, ''),
                email: email ? email : `${generateString(10)}@example.com`,
                phone,
                gender,
                birthdate: moment.utc(birthdate).format('MM/DD/YYYY'),
                zipcode,
                guardianIds: Object.keys(guardians),
                tebraPatientId: tebraId? tebraId.trim() : null,
              });
          }}
          onClickValidation={validate}
          className={button}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

PatientProfile.propTypes = {
  patient: PropTypes.object,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
  backButtonText: PropTypes.string,
};

const styles = () => ({
  input: {
    margin: '0.5rem 0',
    display: 'block',
  },
  button: {
    marginRight: '1em',
    marginTop: '10px'
  },
  warning: {
    color: '#e25141',
    fontSize: '12px',
  },
});

export default withStyles(styles)(PatientProfile);
